import { Messages } from "../../core/api";
import { OfficeId, PlanOfCareTypeId } from "../../shared/schema/schema";

export type PlanOfCareTypeItem = Messages["PlanOfCareTypeItemDetailed"];
export type OfficePlanOfCareSettings = Messages["OfficePlanOfCareSettings"];

/** A row in the settings table: one per office, whether or not it has a plan of care yet. */
export interface OfficePlanOfCareRow {
  officeId: OfficeId;
  officeName: string;
  planOfCareTypeId: PlanOfCareTypeId | null;
  itemCount: number;
}

/**
 * `getOfficesPlanOfCareSettingsOfAgency` filters `is_active IS TRUE` inside the WHERE of a
 * LEFT JOIN, which turns it into an inner join: offices with no plan of care type never come
 * back from that endpoint at all. Build the row list from the offices list instead, so the
 * "Create" action stays reachable.
 */
export function buildOfficeRows(params: {
  offices: { id: OfficeId; name: string }[];
  settings: OfficePlanOfCareSettings[];
}): OfficePlanOfCareRow[] {
  const settingsByOfficeId = new Map(params.settings.map((s) => [s.officeId, s]));

  return params.offices
    .map((office): OfficePlanOfCareRow => {
      const settings = settingsByOfficeId.get(office.id);
      return {
        officeId: office.id,
        officeName: office.name,
        planOfCareTypeId: settings?.planOfCareTypeId ?? null,
        itemCount: settings?.columns.length ?? 0,
      };
    })
    .sort((a, b) => a.officeName.localeCompare(b.officeName));
}

export interface PlanOfCareItemFilters {
  searchTerm: string;
  section: string | null;
  showInactive: boolean;
}

export function filterPlanOfCareItems(
  items: PlanOfCareTypeItem[],
  filters: PlanOfCareItemFilters
): PlanOfCareTypeItem[] {
  const term = filters.searchTerm.trim().toLowerCase();

  return items.filter((item) => {
    if (!filters.showInactive && !item.isActive) {
      return false;
    }
    if (filters.section !== null && item.section !== filters.section) {
      return false;
    }
    if (
      term !== "" &&
      !item.label.toLowerCase().includes(term) &&
      !item.code.toLowerCase().includes(term)
    ) {
      return false;
    }
    return true;
  });
}

export function getSections(items: PlanOfCareTypeItem[]): string[] {
  return [...new Set(items.map((item) => item.section))].sort((a, b) => a.localeCompare(b));
}

/** Unambiguous key for the (section, order) uniqueness checks - sections are free text. */
export function sectionOrderKey(section: string, order: number): string {
  return JSON.stringify([section, order]);
}

/** The next free order number in a section, so a new item lands at the end of it. */
export function getNextOrderInSection(items: PlanOfCareTypeItem[], section: string): number {
  const orders = items.filter((item) => item.section === section).map((i) => i.orderInSection);
  return orders.length === 0 ? 1 : Math.max(...orders) + 1;
}

/**
 * Tell AngularJS to refetch its cached plans of care. `DatabaseApi.getPlansOfCare()` broadcasts
 * `got_plan_of_care_type`, which patientCtrl, dutySheetsCtrl, newVisitCtrl, visitLabelsCtrl,
 * patientsUpcomingTasksCtrl and entityNewVisitComponent all listen for; `PlanOfCareTypeService`
 * caches its list separately and needs an explicit refetch on top of that.
 */
export function emitRefreshPlansOfCare() {
  window.dispatchEvent(
    new CustomEvent("from-webapp-react", {
      detail: { type: "refreshPlansOfCare" },
    })
  );
}
